/**
 * Consumption Schemas
 *
 * This module contains schemas related to energy consumption,
 * operating hours, and consumption history.
 */

import { z } from 'zod';
import { MonthlyConsumptionSchema } from './energy-schemas';

// ============================================
// CONSUMPTION HISTORY
// ============================================

export const ConsumptionHistoryMonthSchema = z.object({
  reference: z.string().nullish(),
  consumptionOffPeakKwh: z.number().nullish(),
  consumptionPeakKwh: z.number().nullish(),
  demandOffPeakKw: z.number().nullish(),
  demandPeakKw: z.number().nullish(),
  totalAmount: z.number().nullish(),
});

export const ConsumptionHistorySchema = z.object({
  months: z.array(ConsumptionHistoryMonthSchema).nullish(),
});

// ============================================
// OPERATING HOURS
// ============================================

const OperatingHoursSchema = z
  .object({
    openingTime: z.string().nullish(),
    closingTime: z.string().nullish(),
  })
  .nullish();

const OperationHoursRangeSchema = z
  .object({
    business: OperatingHoursSchema.nullish(),
    saturday: OperatingHoursSchema.nullish(),
    sunday: OperatingHoursSchema.nullish(),
  })
  .nullish();

// ============================================
// CONSUMPTION PAYLOADS
// ============================================

export const LowVoltageConsumptionPayloadSchema = z.object({
  consumptionLowVoltage: MonthlyConsumptionSchema.nullish(),
  operationHoursRange: OperationHoursRangeSchema.nullish(),
  consumptionOffPeak: MonthlyConsumptionSchema.nullish(),
  consumptionPeak: MonthlyConsumptionSchema.nullish(),
});

export const MediumVoltageConsumptionPayloadSchema = z.object({
  consumptionOffPeak: MonthlyConsumptionSchema.nullish(),
  consumptionPeak: MonthlyConsumptionSchema.nullish(),
});

export const ContractedDemandInfoSchema = z.object({
  contractedDemandPeak: z.number().nullish(),
  contractedDemandOffPeak: z.number().nullish(),
  contractedDemandWithoutDiscountPeak: z.number().nullish(),
  contractedDemandWithoutDiscountOffPeak: z.number().nullish(),
  contractedDemandWithDiscountPeak: z.number().nullish(),
  contractedDemandWithDiscountOffPeak: z.number().nullish(),
});

export const ConsumptionInfoSchema = LowVoltageConsumptionPayloadSchema.merge(
  MediumVoltageConsumptionPayloadSchema,
).extend({
  contractedDemandInfo: ContractedDemandInfoSchema.nullish(),
});
